import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const PRICING_OVERRIDE_REVOKE_ENDPOINT = "/api/pricing/override/revoke";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${value.replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toFixed(2);
};

function AccessDenied() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
      <h1 className="text-2xl font-semibold text-gray-800">Access denied.</h1>
      <p className="mt-2 max-w-md text-sm text-gray-600">
        Your account does not have access to this page. Please contact your administrator.
      </p>
    </div>
  );
}

export default function AdminPricingOverrides() {
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "admin-pricing-overrides");
  const isAdminRole = String(me?.role || "").trim().toLowerCase() === "admin";
  const canRevoke = isAdminRole && permissionLevel === "Full";

  const [filters, setFilters] = useState({
    poNumber: "",
    custCode: "",
    dateFrom: "",
    dateTo: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revokeModal, setRevokeModal] = useState({
    open: false,
    poDetailId: null,
    reason: "",
    error: "",
  });
  const [revokeSaving, setRevokeSaving] = useState(false);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.poNumber.trim()) params.set("po_number", filters.poNumber.trim());
    if (filters.custCode.trim()) params.set("cust_code", filters.custCode.trim());
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    params.set("limit", "200");
    return params.toString();
  }, [filters]);

  const latestOverrideMap = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = row?.po_detail_id;
      if (key === null || key === undefined || map.has(key)) return;
      map.set(key, row);
    });
    return map;
  }, [rows]);

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const resp = await fetch(`${API_BASE}/api/pricing/overrides?${queryParams}`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.error || payload?.message || "Unable to load overrides.");
      }
      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load overrides.");
    } finally {
      setLoading(false);
    }
  }, [me?.email, me?.role, queryParams]);

  useEffect(() => {
    if (permissionLevel === "None") return;
    loadOverrides();
  }, [loadOverrides, permissionLevel]);

  const openRevokeModal = useCallback(
    (poDetailId) => {
      if (!canRevoke || !poDetailId) return;
      setRevokeModal({ open: true, poDetailId, reason: "", error: "" });
    },
    [canRevoke]
  );

  const closeRevokeModal = useCallback(() => {
    if (revokeSaving) return;
    setRevokeModal({ open: false, poDetailId: null, reason: "", error: "" });
  }, [revokeSaving]);

  const handleRevokeReasonChange = (event) => {
    const value = event.target.value;
    setRevokeModal((prev) => ({ ...prev, reason: value, error: "" }));
  };

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeModal.poDetailId) return;
    const reason = String(revokeModal.reason || "").trim();
    if (!reason) {
      setRevokeModal((prev) => ({ ...prev, error: "Reason is required." }));
      return;
    }
    setRevokeSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}${PRICING_OVERRIDE_REVOKE_ENDPOINT}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ po_detail_id: revokeModal.poDetailId, reason }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        console.error("Pricing override revoke failed", { status: resp.status, payload });
        throw new Error(payload?.message || payload?.error || "Unable to revoke override.");
      }
      setRevokeModal({ open: false, poDetailId: null, reason: "", error: "" });
      await loadOverrides();
    } catch (err) {
      console.error("Pricing override revoke request error", err);
      const rawMessage = err?.message || "Unable to revoke override.";
      const friendlyMessage =
        rawMessage === "save_failed" || rawMessage.toLowerCase().includes("save_failed")
          ? "Override revoke failed. Please try again."
          : rawMessage;
      setRevokeModal((prev) => ({ ...prev, error: friendlyMessage }));
    } finally {
      setRevokeSaving(false);
    }
  }, [revokeModal.poDetailId, revokeModal.reason, me?.email, me?.role, loadOverrides]);

  if (permissionLevel === "None") {
    return <AccessDenied />;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">Pricing Overrides</h1>
          <p className="text-sm text-slate-600">Audit log of admin override actions.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadOverrides}
            disabled={loading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          PO#
          <input
            type="text"
            value={filters.poNumber}
            onChange={(event) => setFilters((prev) => ({ ...prev, poNumber: event.target.value }))}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="PO number"
          />
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Cust Code
          <input
            type="text"
            value={filters.custCode}
            onChange={(event) => setFilters((prev) => ({ ...prev, custCode: event.target.value }))}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Cust Code"
          />
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Date From
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Date To
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
        <div className="md:col-span-4 flex justify-end">
          <button
            type="button"
            onClick={loadOverrides}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Search
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-auto border rounded-md bg-white">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">PO#</th>
              <th className="px-3 py-2 text-left font-semibold">Line#</th>
              <th className="px-3 py-2 text-left font-semibold">Cust Code</th>
              <th className="px-3 py-2 text-left font-semibold">Walmart Item No</th>
              <th className="px-3 py-2 text-left font-semibold">Expected</th>
              <th className="px-3 py-2 text-left font-semibold">Actual</th>
              <th className="px-3 py-2 text-left font-semibold">Reason</th>
              <th className="px-3 py-2 text-left font-semibold">By</th>
              <th className="px-3 py-2 text-left font-semibold">At</th>
              <th className="px-3 py-2 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  Loading overrides...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  No overrides found.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const latestRow = latestOverrideMap.get(row?.po_detail_id);
                const canRevokeRow =
                  canRevoke &&
                  latestRow &&
                  latestRow.id === row.id &&
                  String(row.override_action || "").toUpperCase() === "ALLOW_MISMATCH";
                return (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">{row.po_number || "—"}</td>
                    <td className="px-3 py-2">{row.line_number || "—"}</td>
                    <td className="px-3 py-2">{row.cust_code || "—"}</td>
                    <td className="px-3 py-2">{row.walmart_item_no || "—"}</td>
                    <td className="px-3 py-2">{formatNumber(row.expected_price)}</td>
                    <td className="px-3 py-2">{formatNumber(row.actual_price)}</td>
                    <td className="px-3 py-2">{row.reason || "—"}</td>
                    <td className="px-3 py-2">{row.created_by_email || row.created_by_user_id || "—"}</td>
                    <td className="px-3 py-2">{formatTimestamp(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>{row.override_action || "—"}</span>
                        {canRevokeRow && (
                          <button
                            type="button"
                            onClick={() => openRevokeModal(row.po_detail_id)}
                            className="rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {revokeModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={closeRevokeModal} />
          <div className="relative z-50 w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Revoke Override</h2>
              <p className="mt-1 text-sm text-slate-600">
                Remove pricing override for PO line{" "}
                <span className="font-semibold text-slate-900">#{revokeModal.poDetailId}</span>.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Reason
                <textarea
                  value={revokeModal.reason}
                  onChange={handleRevokeReasonChange}
                  rows={3}
                  className="mt-1 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Provide the reason for revoking the override"
                  disabled={revokeSaving}
                />
              </label>
              {revokeModal.error && <p className="text-xs text-red-600">{revokeModal.error}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeRevokeModal}
                disabled={revokeSaving}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRevoke}
                disabled={revokeSaving}
                className="rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {revokeSaving ? "Saving..." : "Confirm Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
